'use client';

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import { statics } from '@/data/store';
import FilterCardGrid from './FilterCardGrid';
import FilterTable from './FilterTable';
import { COLORS } from '@/constants/colors';
import { SPACING } from '@/constants/spacing';
import { BREAKPOINTS } from '@/constants/breakpoints';
import LoadingOverlay from '@/components/LoadingOverlay';
import type { FilterProduct } from '@/types/api';


const tableHeader = [
  {
    id: 0,
    name: 'title',
    label: 'Filter Number',
    maxWidth: 161,
  },
  {
    id: 1,
    name: 'filter_od_length_inch',
    label: 'Outside Diameter (OD)',
    maxWidth: 277,
  },
  {
    id: 2,
    name: 'filter_id_length_inch',
    label: 'Inside Diameter (ID)',
    maxWidth: 255,
  },
  {
    id: 3,
    name: 'filter_height_inch',
    label: 'Height (H)',
    maxWidth: 143,
  },
  {
    id: 4,
    label: 'Shape',
    name: 'filter_shape',
    maxWidth: 236,
  },
  {
    id: 5,
    name: 'filter_color',
    label: 'Color',
    maxWidth: 130,
  },
];

type ViewMode = 'cards' | 'table';

const URElement1 = () => {
  const [tableData, setTableData] = useState<FilterProduct[]>([]);
  const [loading, setLoading] = useState(false);
  // Defaults to table on desktop, cards on mobile/tablet; overridden once the user picks manually.
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [userSelectedView, setUserSelectedView] = useState(false);

  useEffect(() => {
    getTableData();
  }, []);

  useEffect(() => {
    if (userSelectedView) return;

    const mql = window.matchMedia(`(max-width: ${BREAKPOINTS.tablet})`);
    const applyDefaultView = () => setViewMode(mql.matches ? 'cards' : 'table');
    applyDefaultView();

    mql.addEventListener('change', applyDefaultView);
    return () => mql.removeEventListener('change', applyDefaultView);
  }, [userSelectedView]);

  const handleViewChange = (mode: ViewMode) => {
    setUserSelectedView(true);
    setViewMode(mode);
  };

  const getTableData = async () => {
    setLoading(true);
    const product_type_id = 4;

    const url = `${statics.BaseUrl}/product?product_type_id=${product_type_id}&pageNo=1&pageSize=200&is_active=1`;

    try {
      const res = await fetch(url);
      const data = await res.json();
      if (data?.ResponseCode === 'Success') {
        const rows = Array.isArray(data?.data) ? data.data : data?.data?.rows || [];
        setTableData(rows);
      }
    } catch (error) {
      console.error('Failed to fetch round filters:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingOverlay loading={loading} />

      <URElement1Wrapper>
        <PageTitle>Round Air Filters</PageTitle>

        <IntroText>
          Discover our complete selection of round air filters designed for performance applications. Whether you need
          a round filter for your engine or an oval air filter variation, Green Filter has you covered. Browse our
          round air filter by size guide to find the perfect round engine air filter - all made in the USA with our
          lifetime warranty.
        </IntroText>

        <div
          style={{
            textAlign: 'center',
            float: 'left',
            width: '30%',
            minWidth: 300,
          }}
        >
          <Image
            src="/assets/cylinders/round.webp"
            alt="Round Air Filters"
            width={400}
            height={400}
            priority
            style={{ width: '100%', height: 'auto' }}
          />
        </div>

        <div
          style={{
            textAlign: 'center',
            float: 'left',
            width: '20%',
            minWidth: 300,
          }}
        >
          <center>
            <a href="/assets/cylinders/round_filter_measurements.webp" style={{ color: COLORS.brandGreen }}>
              <Image
                src="/assets/cylinders/round_filter_measurements.webp"
                alt="Round Filter Measurements"
                width={300}
                height={300}
                style={{ width: '100%', height: 'auto' }}
              />
            </a>
            <br />
            <a
              href="/assets/cylinders/round_filter_measurements.webp"
              style={{ color: COLORS.brandGreen }}
              title="Green Filter USA - Round Filter Dimensions"
            >
              Click here
            </a>{' '}
            for a picture showing how round filters are measured.
          </center>
        </div>
        <div style={{ clear: 'both', display: 'table' }}></div>
        <br />

        <h4 style={{ padding: '0 10px' }}>
          {viewMode === 'cards'
            ? 'Select a filter below, or use the sort and filter options to narrow down by shape or color.'
            : 'Select a filter from the list below. The list can be sorted by clicking on a column heading. Clicking the heading again will sort it in the opposite direction. The table will scroll left and right for smaller screens.'}
        </h4>
        <p style={{ textAlign: 'center' }}>
          <strong>All measurements are in inches.</strong>
        </p>

        <ViewToggle>
          <ViewToggleButton $active={viewMode === 'table'} onClick={() => handleViewChange('table')}>
            Table
          </ViewToggleButton>
          <ViewToggleButton $active={viewMode === 'cards'} onClick={() => handleViewChange('cards')}>
            Cards
          </ViewToggleButton>
        </ViewToggle>

        {viewMode === 'cards' ? (
          <FilterCardGrid
            tableData={tableData}
            styleField="filter_shape"
            odField="filter_od_length_inch"
            defaultSortBy="od-asc"
          />
        ) : (
          <FilterTable
            headers={tableHeader}
            tableData={tableData}
            defaultOrderBy="filter_od_length_inch"
            showAddToCart
          />
        )}

        <ClosingSection>
          <ClosingText>
            Need help finding the right round filter? Use our{' '}
            <a href="/shop-by-vehicle">vehicle selector</a> or{' '}
            <a href="/contact-us">contact us</a> for expert guidance on round air filters.
          </ClosingText>
        </ClosingSection>
      </URElement1Wrapper>
    </>
  );
};

export default React.memo(URElement1);

const ViewToggle = styled.div`
  display: flex;
  justify-content: center;
  gap: ${SPACING.xs};
  margin-bottom: ${SPACING.lg};
`;

const ViewToggleButton = styled.button<{ $active: boolean }>`
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 24px;
  border-radius: 20px;
  border: 1px solid ${(props) => (props.$active ? COLORS.brandGreen : COLORS.border)};
  background: ${(props) => (props.$active ? COLORS.brandGreen : COLORS.white)};
  color: ${(props) => (props.$active ? COLORS.white : COLORS.textPrimary)};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${COLORS.brandGreen};
  }
`;

const IntroText = styled.p`
  max-width: 800px;
  margin: 0 auto 30px;
  padding: 0 20px;
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #555;
  line-height: 1.7;
  text-align: center;

  @media (max-width: 767px) {
    font-size: 15px;
    margin-bottom: 20px;
  }
`;

const ClosingSection = styled.div`
  max-width: 800px;
  margin: 30px auto 50px;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
  text-align: center;

  @media (max-width: 767px) {
    margin: 20px auto 30px;
    padding: 15px;
  }
`;

const ClosingText = styled.p`
  font-family: Lato, sans-serif;
  font-size: 15px;
  color: #555;
  margin: 0;
  line-height: 1.6;

  a {
    color: ${COLORS.brandGreen};
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

const PageTitle = styled.h1`
  font-family: Lato, sans-serif;
  font-size: 38px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 20px 20px;

  @media (max-width: 767px) {
    font-size: 26px;
    margin: 0 0 16px;
  }
`;

const URElement1Wrapper = styled.div`
  padding: 20px;
  background: #fff;

  h2 {
    font-family: Lato, sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 30px;
  }

  h4 {
    font-family: Lato, sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #333;
    line-height: 1.6;
    margin-bottom: 20px;
  }

  p {
    font-family: Lato, sans-serif;
    font-size: 15px;
    color: #333;
    margin-bottom: 30px;
  }

  .resize {
    max-width: 100%;
    height: auto;
  }

  center {
    font-family: Lato, sans-serif;
    font-size: 14px;

    a {
      font-weight: 600;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  @media (max-width: 767px) {
    padding: 15px;

    h2 {
      font-size: 22px;
    }

    h4 {
      font-size: 14px;
    }

    div[style*='float: left'] {
      float: none !important;
      width: 100% !important;
      min-width: auto !important;
      margin-bottom: 20px;
    }
  }
`;
